/** Declarative tour specs. Add a tour by appending an entry — the engine
 *  reads everything off this registry. Each step targets a DOM element by
 *  its `data-tour-target` attribute (added in the relevant component). */

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

export interface TourStep {
  /** Stable id for analytics + skip-to-step links. */
  id: string;
  /** `data-tour-target` value to anchor the popover at. Use `null` for a
   *  centered "intro" step that doesn't point at anything. */
  target: string | null;
  title: string;
  body: string;
  /** Optional path to navigate to before showing this step. The engine will
   *  push history and wait one frame so the target mounts. */
  path?: string;
  /** Where the popover sits relative to the target. Defaults to `bottom`.
   *  When `target` is null the placement is forced to `center`. */
  placement?: TourPlacement;
}

export interface TourSpec {
  id: string;
  name: string;
  /** Where this tour appears in the "Take a tour" picker. */
  description: string;
  /** First-visit-of trigger. The engine auto-starts the tour when the
   *  user first visits this path AND has not completed the tour. Leave
   *  undefined for tours that only run via manual trigger. */
  autoStartOnPath?: string;
  steps: TourStep[];
}

/** Welcome tour — fires once on first authenticated dashboard visit.
 *  Walks the canonical reseller workflow (connect → add → publish →
 *  watch) instead of the UI chrome. The UI tour ("Where things live")
 *  is still reachable from ⌘K → Take a tour as `shellTour` below for
 *  users who prefer the layout walkthrough. */
const welcomeTour: TourSpec = {
  id: 'welcome',
  name: 'Welcome to For Visibility',
  description: 'A 4-step walkthrough of how to get from zero to your first sale.',
  autoStartOnPath: '/',
  steps: [
    {
      id: 'intro',
      target: null,
      title: 'Welcome to For Visibility',
      body: "Let's walk through how you'll actually use this — from connecting eBay to seeing your first sale land. 4 steps, takes about a minute. You can skip any time and reopen from ⌘K → Take the tour.",
    },
    {
      id: 'connect-ebay',
      target: 'settings-integrations',
      path: '/settings/integrations',
      title: 'Step 1 — Connect eBay',
      body: "First thing: connect your eBay seller account. Publishing, pulling orders, syncing returns, and 1099-K reporting all flow through this connection. We'll wait for you to click 'Connect eBay' here, then come back to this tour.",
      placement: 'bottom',
    },
    {
      id: 'add-inventory',
      target: 'inventory-actions',
      path: '/inventory',
      title: 'Step 2 — Add an inventory item',
      body: "Inventory is your private catalog — everything you've sourced, with cost basis, condition, and photos. Click 'Add Item' to create your first one. (You can also bulk-import from a CSV.)",
      placement: 'bottom',
    },
    {
      id: 'publish-to-ebay',
      target: 'inventory-actions',
      path: '/inventory',
      title: 'Step 3 — Publish to eBay',
      body: "Once you've added an item, click the ✨ icon on its row. We'll AI-generate a listing draft you can edit, pick a category, fill required item specifics, and publish to eBay in one click. Repeat for as many items as you like.",
      placement: 'bottom',
    },
    {
      id: 'watch-for-sales',
      target: 'dashboard-kpi-bar',
      path: '/',
      title: 'Step 4 — Watch for sales',
      body: "When buyers purchase, sales land here within seconds (via webhook) or minutes (via the 5-minute poll fallback). The KPI strip shows live revenue, profit, and pending shipments. Returns and best offers surface on the Listings page. You're set.",
      placement: 'bottom',
    },
  ],
};

/** "Where things live" — the old chrome-walking tour, kept available
 *  as a secondary tour for users who want a layout-only orientation
 *  after they're already shipping. Reachable via ⌘K → Take a tour. */
const shellTour: TourSpec = {
  id: 'shell',
  name: 'Where things live',
  description: 'A 60-second tour of the workspace — sidebar and command palette.',
  steps: [
    {
      id: 'sidebar',
      target: 'sidebar-nav',
      title: 'Your workspaces',
      body: 'Dashboard, Inventory, Listings, Messages, Analytics, Automation, Agents, Sourcing, Settings. Click the chevron at the bottom to collapse the sidebar.',
      placement: 'right',
    },
    {
      id: 'cmdk',
      target: 'header-search',
      title: 'Jump to anything with ⌘K',
      body: 'Open the command palette to search the app and jump between pages. Works anywhere — not just here.',
      placement: 'bottom',
    },
    {
      id: 'ai-mode',
      target: 'header-ai-mode',
      title: 'Automations',
      body: 'Agents act only through automation rules you switch on, like offer or return rules. This shows how many are on; click it to manage them. Everything an agent does is logged under Agents, with undo where eBay allows it.',
      placement: 'bottom',
    },
    {
      id: 'action-queue',
      target: 'dashboard-action-queue',
      title: 'Needs attention',
      body: 'Items sitting unsold for 30+ and 60+ days, and anything reserved or on hold, show up here from your own inventory. Click a row to go deal with it.',
      placement: 'top',
    },
  ],
};

/** Inventory deep-dive — fires on first visit to /inventory. */
const inventoryTour: TourSpec = {
  id: 'inventory',
  name: 'Inventory deep dive',
  description: 'Stats, filters, and how to add items in bulk.',

  steps: [
    {
      id: 'mini-stats',
      target: 'inventory-mini-stats',
      title: 'Stock health at a glance',
      body: 'Total / Available / Listed / Sold. Watch the Listed bucket — items here are working for you across platforms.',
      placement: 'bottom',
    },
    {
      id: 'add-import',
      target: 'inventory-actions',
      title: 'Add one or import many',
      body: 'Use Add Item for a single item with photos. Import CSV for bulk migration — required columns are name, cost_basis, condition, category.',
      placement: 'left',
    },
    {
      id: 'tabs',
      target: 'inventory-tabs',
      title: 'AI features live in the tabs',
      body: 'AI Pricing recommends list prices from comps. Market Trends spots categories on the move. Appraisal estimates value for high-ticket pieces.',
      placement: 'bottom',
    },
  ],
};

/** Listings deep-dive — manual trigger via ⌘K only. */
const listingsTour: TourSpec = {
  id: 'listings',
  name: 'Listings deep dive',
  description: 'Cross-platform sync and AI-generated drafts.',
  steps: [
    {
      id: 'mini-stats',
      target: 'listings-mini-stats',
      title: 'Cross-platform totals',
      body: 'Total counts across every platform you sell on. The Active number is what is currently visible to buyers.',
      placement: 'bottom',
    },
    {
      id: 'sync',
      target: 'listings-sync',
      title: 'Sync from eBay (and more)',
      body: 'Pull fresh status, views, and watchers from each connected platform. Runs automatically every few minutes; click for an immediate refresh.',
      placement: 'bottom',
    },
  ],
};

/** Sourcing tour — fires on first visit to /sourcing. Sourcing is a
 *  mobile-first capture flow (camera + offline queue), so the tour is
 *  short and explains the moving parts a desk visitor may miss. */
const sourcingTour: TourSpec = {
  id: 'sourcing',
  name: 'Sourcing in the field',
  description: 'Identify items, get comp pricing, queue offline.',

  steps: [
    {
      id: 'intro',
      target: null,
      title: 'Sourcing on the go',
      body: 'Sourcing is built for the field — snap a photo of an item, the AI identifies it, and you see comparable sold prices before you commit. Works offline; syncs when you reconnect.',
    },
    {
      id: 'connection',
      target: 'sourcing-connection',
      title: 'Online vs offline',
      body: 'You can keep capturing items even with no signal. Anything captured offline queues here and uploads automatically once you reconnect. The Sync button forces an immediate upload.',
      placement: 'bottom',
    },
  ],
};

/** Analytics tour — fires on first visit to /analytics. */
const analyticsTour: TourSpec = {
  id: 'analytics',
  name: 'Analytics walkthrough',
  description: 'Revenue, profit, and turn metrics.',

  steps: [
    {
      id: 'kpis',
      target: 'analytics-kpis',
      title: 'Top-line numbers',
      body: 'Total Revenue is all-time. Gross Profit is the dollar margin; the percentage underneath is your blended margin. Items Sold counts unique items moved across every channel. Avg Turns is items per transaction — higher means you bundle effectively.',
      placement: 'bottom',
    },
  ],
};

/** Automation tour — fires on first visit to /automation. */
const automationTour: TourSpec = {
  id: 'automation',
  name: 'Automation rules',
  description: 'Trigger agent actions automatically.',

  steps: [
    {
      id: 'intro',
      target: null,
      title: 'Let agents do the boring parts',
      body: 'Automation rules trigger agent actions when conditions are met — like dropping price after 30 days, cross-listing winners to a second platform, or auto-declining lowball offers. A rule only acts while it is switched on; the header shows how many are on, and every action is logged under Agents.',
    },
    {
      id: 'new-rule',
      target: 'automation-new-rule',
      title: 'Start with a template',
      body: 'Click New Rule to build from scratch, or use the suggestions on first run to enable a starter pack — they cover ~80% of common reseller workflows.',
      placement: 'left',
    },
  ],
};

/** Settings tour — focused on the eBay connection because that is the
 *  highest-value first-run setup task. Auto-fires on /settings the first
 *  time the user lands there. */
const settingsTour: TourSpec = {
  id: 'settings',
  name: 'Settings + integrations',
  description: 'Connect eBay, configure account, manage billing.',

  steps: [
    {
      id: 'intro',
      target: null,
      title: 'Settings tour',
      body: "We'll point out the spots that matter most for a first-run setup — connecting eBay is the biggest leverage, since it unlocks listing, sync, and pricing comps.",
    },
    {
      id: 'integrations',
      target: 'settings-integrations',
      title: 'Connect your sales channels',
      body: 'eBay Marketplace lights up listing publishing, comp lookups, and transaction sync. The OAuth flow takes 30 seconds — eBay redirects back here when done.',
      placement: 'bottom',
    },
  ],
};

/** Messages tour — fires on first visit to /messages. The Messages
 *  page is a split-pane layout (conversation list + thread view), so
 *  the tour stays brief and uses anchored steps for the structure. */
const messagesTour: TourSpec = {
  id: 'messages',
  name: 'Customer messages',
  description: 'Buyer threads + AI-suggested replies.',

  steps: [
    {
      id: 'intro',
      target: null,
      title: 'Customer messages, all in one inbox',
      body: "Every buyer message across every connected platform lands here. Click a thread to read and reply — the system delivers your reply back to the right platform's inbox.",
    },
    {
      id: 'conversation-list',
      target: 'messages-conversation-list',
      title: 'Conversation list',
      body: 'Buyer threads are sorted by recency. Unread threads bubble to the top with a dot. Refresh pulls fresh messages from each connected platform.',
      placement: 'right',
    },
  ],
};

/** Agents tour — fires on first visit to /agents/activity. Explains the
 *  audit log + undo window that backs every agent and automation action. */
const agentsTour: TourSpec = {
  id: 'agents',
  name: 'Agent activity',
  description: 'Audit log + undo for autonomous AI actions.',

  steps: [
    {
      id: 'intro',
      target: null,
      title: 'Every agent action, recorded',
      body: 'When AI agents act on your behalf — repricing items, cross-listing, replying to offers — each action is logged here. You can review what happened and roll back any action within 30 days.',
    },
    {
      id: 'mini-stats',
      target: 'agents-mini-stats',
      title: 'Activity at a glance',
      body: 'All time = every agent action since signup. Today = actions in the last 24 hours. Undone = actions you rolled back. Watch the Undone count for signals that an agent rule needs tightening.',
      placement: 'bottom',
    },
  ],
};

export const TOURS: TourSpec[] = [
  welcomeTour,
  shellTour,
  inventoryTour,
  listingsTour,
  sourcingTour,
  analyticsTour,
  automationTour,
  messagesTour,
  agentsTour,
  settingsTour,
];

export function getTourById(id: string): TourSpec | undefined {
  return TOURS.find((t) => t.id === id);
}
